import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@common/components/DatePicker/DatePicker";
import { SelectAnyEmployeeForm } from "@common/components/EmployeeSelect/SelectAnyEmployeeForm";
import { Button } from "@/components/ui/button";

import { Textarea } from "@/components/ui/textarea";
import { useVisibleExpenseCategoriesQuery } from "@common/utils/graphql";
import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { Body } from "@hypercard/web-components";
import { CurrencyInput } from "@common/components/CurrencyInput";
import { cn } from "@/utils/utils";
import { ReceiptUploadDropzone } from "@common/components";
import { ExpenseFormValues } from "../forms/Forms";
import { CustomFormFields } from "@common/components/CustomFields";

interface Props {
  form: UseFormReturn<ExpenseFormValues>;
  onSubmit: () => void;
  submitText?: string;
  loading?: boolean;
  className?: string;
  onParseReceipt: (params: Partial<ExpenseFormValues>) => void;
}

export const ReimbursementDetailsForm = ({
  form,
  onSubmit,
  submitText = "Next Step",
  loading: saving = false,
  className,
  onParseReceipt,
}: Props) => {
  const { data: categoryDataRaw, loading: loadingCategories } =
    useVisibleExpenseCategoriesQuery({
      fetchPolicy: "cache-and-network",
    });

  const loading = saving || loadingCategories;

  const categories = useMemo(() => {
    if (
      categoryDataRaw?.visibleExpenseCategories?.__typename !==
      "QueryVisibleExpenseCategoriesSuccess"
    ) {
      return [];
    }
    return categoryDataRaw.visibleExpenseCategories.data.categories;
  }, [categoryDataRaw]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn("flex flex-1 flex-col space-y-4", className)}
      >
        {/* Receipt upload */}
        <FormField
          control={form.control}
          name="receipt"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Receipt</FormLabel>
              <FormControl>
                <ReceiptUploadDropzone
                  onParseReceipt={onParseReceipt}
                  field={field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Merchant */}
        <FormField
          control={form.control}
          name="merchant"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant</FormLabel>
              <FormControl>
                <Input placeholder="Merchant" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <CustomFormFields form={form} loading={loading} />

        {/* Expense category GL ID */}
        <FormField
          control={form.control}
          name="expenseCategoryGLId"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        <Body
                          size="sm"
                          weight="regular"
                          className="text-muted-foreground"
                        >
                          Select a category
                        </Body>
                      }
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem value={c.id.toString()} key={c.id}>
                      {c.categoryName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex space-x-4">
          {/* Amount */}
          <FormField
            control={form.control}
            name="amount"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormControl className="w-32">
                  <CurrencyInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Date picker */}
          <FormField
            control={form.control}
            name="date"
            disabled={loading}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Transaction Date</FormLabel>
                <FormControl>
                  <DatePicker
                    selected={field.value}
                    onSelect={(value) => {
                      field.onChange(value);
                    }}
                    disabled={loading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex justify-between" disableAsterisk>
                <span>Notes</span>
                <span>Optional</span>
              </FormLabel>
              <FormControl>
                <Textarea placeholder="e.g. Car rented in Florida" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Attendee emails */}
        <FormField
          control={form.control}
          disabled={loading}
          name="attendeeEmails"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Attendees</FormLabel>
              <FormControl>
                <SelectAnyEmployeeForm
                  placeholder="Select attendees"
                  onChange={(newList) =>
                    field.onChange(newList.map((list) => list.workEmail))
                  }
                  defaultValues={form.watch("attendeeEmails")}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex-1" />

        {/* Done button */}
        <Button type="submit" disabled={loading}>
          {submitText}
        </Button>
      </form>
    </Form>
  );
};
